import React, { useCallback, useMemo, useState } from 'react';
import { useWatchCasted } from 'utils/hooks/useWatchCasted';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { SubmitHandler } from 'react-hook-form';
import * as yup from 'yup';
import { isAfter, isBefore } from 'date-fns';

import { EnterMedicaidInsuranceForm } from 'domains/signup/data/types';
import { SelectInput, Form, FormField, TextInput, SubmitButton, SelectOption } from 'components/Forms';
import { CheckboxInput } from 'components/Forms/inputs/CheckboxInput';
import { Heading } from 'components/Typography';
import { InsuranceContextV4, withInsuranceMachineStateV4 } from 'domains/signupv4/Insurance/context';
import { getPersonAge } from 'utils/date';
import { formatInTimeZone } from 'domains/dates/format';

const MIN_VALID_DATE = new Date(1900, 0, 0);

type Props = {
  onSubmit: SubmitHandler<EnterMedicaidInsuranceForm>;
  insuranceOptions?: SelectOption[];
  defaultValues?: EnterMedicaidInsuranceForm;
  notInsuranceAvailable?: boolean;
};

const getFormSchema = (t: TFunction) =>
  yup.object({
    notInsuranceAvailable: yup.boolean().required(t('form.validation.fieldIsRequired')),
    insurancePlan: yup.string().when('notInsuranceAvailable', {
      is: false,
      then: schema => schema.required(t('form.validation.fieldIsRequired')),
      otherwise: schema => schema.notRequired(),
    }),
    firstName: yup
      .string()
      .trim()
      .when('notInsuranceAvailable', {
        is: false,
        then: schema => schema.required(t('form.validation.fieldIsRequired')),
        otherwise: schema => schema.notRequired(),
      }),
    lastName: yup
      .string()
      .trim()
      .when('notInsuranceAvailable', {
        is: false,
        then: schema => schema.required(t('form.validation.fieldIsRequired')),
        otherwise: schema => schema.notRequired(),
      }),
    dob: yup.string().when('notInsuranceAvailable', {
      is: false,
      then: schema => schema.required(t('form.validation.fieldIsRequired')),
      otherwise: schema => schema.notRequired(),
    }),
    memberId: yup
      .string()
      .trim()
      .when('notInsuranceAvailable', {
        is: false,
        then: schema => schema.required(t('form.validation.fieldIsRequired')),
        otherwise: schema => schema.notRequired(),
      }),
    groupId: yup.string().trim().optional(),
  });

const EnterMedicaidInsuranceInfo: React.FC<Props> = ({
  onSubmit,
  insuranceOptions,
  defaultValues,
  notInsuranceAvailable,
}) => {
  const { t } = useTranslation();
  const [notInsuranceAvailableLocalState, setNotInsuranceAvailableLocalState] = useState<boolean>(
    !!notInsuranceAvailable,
  );

  return (
    <>
      <Form
        onSubmit={onSubmit}
        schema={getFormSchema(t)}
        validationMode="all"
        flashErrors={false}
        defaultValues={defaultValues}
      >
        <Heading level="h2" className="mb-16">
          {t('signup.direct.steps.enterMedicaidInsuranceInfo.title')}
        </Heading>
        <Heading level="h7" className="mb-64 sm-d:mb-40">
          {t('signup.direct.steps.enterMedicaidInsuranceInfo.description')}
        </Heading>
        <Heading level="h4" className="mb-16">
          {t('signup.direct.steps.enterMedicaidInsuranceInfo.subtitle')}
        </Heading>
        <Heading level="h8" className="mb-40 sm-d:mb-40">
          {t('signup.direct.steps.enterMedicaidInsuranceInfo.subdescription')}
        </Heading>
        <div className="mb-64 sm-d:mb-40">
          <FormField
            data-testid="not-insurance-available-input"
            component={CheckboxInput}
            name="notInsuranceAvailable"
            id="notInsuranceAvailable"
            option={{
              label: t('signup.direct.steps.enterMedicaidInsuranceInfo.notInsuranceAvailable'),
              value: 'true',
              startAdornment: true,
            }}
            onChange={() => {
              setNotInsuranceAvailableLocalState(!notInsuranceAvailableLocalState);
            }}
          />
        </div>
        {!notInsuranceAvailableLocalState && (
          <>
            <div className="mb-40 sm-d:mb-40">
              <FormField
                allowEmpty
                component={SelectInput}
                name="insurancePlan"
                data-testid="insurance-plan-to-select"
                label={t('signup.direct.steps.enterMedicaidInsuranceInfo.plan') as string}
                options={insuranceOptions ?? []}
              />
            </div>
            <div className="gap-24 mb-20 grid grid-cols-2 sm-d:grid-rows-2 sm-d:grid-cols-1">
              <FormField
                data-testid="first-name-input"
                component={TextInput}
                name="firstName"
                label={t('signup.direct.steps.enterMedicaidInsuranceInfo.subscriberFirstName') as string}
              />
              <FormField
                data-testid="last-name-input"
                component={TextInput}
                name="lastName"
                label={t('signup.direct.steps.enterMedicaidInsuranceInfo.subscriberLastName') as string}
              />
            </div>
            <div className="gap-24 mb-20 grid grid-cols-2 sm-d:grid-rows-2 sm-d:grid-cols-1">
              <DOBForm />
            </div>
            <div className="gap-24 mb-20 grid grid-cols-2 sm-d:grid-rows-2 sm-d:grid-cols-1">
              <FormField
                data-testid="member-id"
                component={TextInput}
                name="memberId"
                label={t('signup.direct.steps.enterMedicaidInsuranceInfo.memberId') as string}
              />
              <FormField
                data-testid="group-id-input"
                component={TextInput}
                name="groupId"
                label={t('signup.direct.steps.enterMedicaidInsuranceInfo.groupId') as string}
              />
            </div>
          </>
        )}

        <SubmitButton
          data-testid="insurance-info-submit-button"
          size="lg"
          className={!notInsuranceAvailable ? 'mt-64 sm-d:mt-40 sm-d:w-full' : 'mt-0 sm-d:mt-0 sm-d:w-full'}
          willRedirect={false}
          disableIfInvalid
        >
          {t('signup.direct.steps.enterInsuranceType.submit')}
        </SubmitButton>
      </Form>
    </>
  );
};

const DOBForm: React.FC = () => {
  const { t } = useTranslation();
  const value = useWatchCasted<Date>('dob');
  const formattedAge = useMemo(() => getPersonAge(t, value), [value, t]);

  return (
    <FormField
      data-testid="dob-input"
      component={TextInput}
      label={t('signup.direct.steps.enterMedicaidInsuranceInfo.subscriberDOB') as string}
      hint={
        value && isBefore(value, new Date()) && isAfter(value, MIN_VALID_DATE)
          ? (t('signup.direct.steps.enterMedicaidInsuranceInfo.ageHint', { age: formattedAge }) as string)
          : undefined
      }
      name="dob"
      type="date"
      max={formatInTimeZone(new Date(), 'UTC', 'yyyy-MM-dd')}
    />
  );
};

const InsuranceWrapper = () => {
  const { send } = InsuranceContextV4.useActorRef();
  const {
    acceptedInsuranceEntries: insuranceEntries,
    acceptedInsuranceOptions: insuranceOptions,
    insuranceForm: insuranceInfo,
  } = InsuranceContextV4.useSelector(({ context }) => context.data);

  const onSubmit: SubmitHandler<EnterMedicaidInsuranceForm> = useCallback(
    ({ firstName, lastName, insurancePlan, notInsuranceAvailable, dob, memberId, groupId }) => {
      const selectedEntry = insuranceEntries?.find(entry => entry.payerId === insurancePlan);

      send({
        type: 'submitInsuranceInfo',
        insuranceInfo: {
          firstName,
          lastName,
          payerName: selectedEntry?.candidPayerName as string,
          payerId: insurancePlan,
          insuranceName: selectedEntry?.insuranceName as string,
          insurancePlan,
          notInsuranceAvailable,
          dob,
          memberId,
          groupId,
          entity: selectedEntry?.entity as string,
        },
      });
    },
    [insuranceEntries, send],
  );

  const defaultValues: EnterMedicaidInsuranceForm = {
    notInsuranceAvailable: false,
    firstName: '',
    lastName: '',
    insuranceName: '',
    insurancePlan: '',
    payerId: '',
    payerName: '',
    dob: undefined,
    memberId: '',
    groupId: '',
    entity: '',
    ...insuranceInfo,
  };

  return (
    <EnterMedicaidInsuranceInfo
      onSubmit={onSubmit}
      insuranceOptions={insuranceOptions}
      defaultValues={defaultValues}
      notInsuranceAvailable={insuranceInfo?.notInsuranceAvailable}
    />
  );
};

export const InsuranceV4EnterInsuranceInfo = withInsuranceMachineStateV4(
  InsuranceWrapper,
  'enterMedicaidInsuranceInfo',
);
